/*
Admin page — listing of every user with role + disabled / locked
status, plus inline actions (promote / demote, disable / re-enable,
unlock, delete) and a small create-user form.

Calls /api/admin/users with ?page=&pageSize= for pagination,
?email= for substring search, and ?sort=&order= for sortable
column headers (email / role / createdAt / cardCount). Search is
debounced 250ms; clicking a sorted header toggles asc/desc. Either
event resets the cursor to page 1.

URL-persistent: search / sort / order / pageSize are mirrored to the
URL search string (e.g. /admin/users?email=foo&sort=cardCount&order=desc)
so a filtered or sorted view is bookmarkable. URL writes use
replace=true so each keystroke doesn't push a new history entry.
*/

import { useCallback, useEffect, useRef, useState, type FormEvent, type JSX } from "react";
import { useSearchParams } from "react-router-dom";

import { useTranslation } from "react-i18next";

import { ApiError, api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useConfirmDialog } from "../components/ConfirmDialog";
import { ErrorState } from "../components/ErrorState";
import { Skeleton, SkeletonRows } from "../components/Skeleton";

const kPageSizeDefault	= 50;					// Initial rows per page
const kPageSizeOptions	= [25, 50, 100, 200];	// Selector choices
const kSearchDebounceMs	= 250;					// Settle delay before re-issuing a filtered request

type SortKey	= "email" | "role" | "createdAt" | "cardCount";	// Sortable columns
type SortOrder	= "asc" | "desc";									// Sort direction

// Default direction per column — newest createdAt first, alpha A→Z
// for everything else, admins-first for role. Matches the server's
// per-key defaults so the first click on a header picks up the
// "expected" direction without an extra round-trip.
const kDefaultOrders: Record<SortKey, SortOrder> =
{
	email:		"asc",
	role:		"desc",
	createdAt:	"desc",
	cardCount:	"desc",
};

interface UserRow
{
	id:						string;	// User id
	email:					string;	// Email address
	role:					"USER" | "ADMIN";
	createdAt:				number;	// Created (ms since epoch)
	updatedAt:				number;	// Last update (ms since epoch)
	disabledAt:				number | null;	// null when active
	lockedUntil:			number | null;	// null when not locked, ms-since-epoch otherwise
	failedLoginAttempts:	number;	// Current consecutive-failure counter
	cardCount:				number;	// Number of card contents owned
}

interface UsersPageResponse
{
	items:		UserRow[];	// Paginated rows
	total:		number;		// Unpaginated total
	page:		number;		// Echoed page (after default + clamp)
	pageSize:	number;		// Echoed pageSize
}

export function AdminUsersPage(): JSX.Element
{
	const { userId: vMeId } = useAuth();	// Authenticated user id (current admin)
	const fConfirm = useConfirmDialog();
	const { t }		= useTranslation("admin_users");
	const { t: tc }	= useTranslation("common");

	const [vSearchParams, fSetSearchParams] = useSearchParams();

	const [fUsers, fSetUsers]		= useState<UserRow[] | null>(null);
	const [fTotal, fSetTotal]		= useState<number>(0);
	const [fPage,  fSetPage]		= useState<number>(1);
	const [fError, fSetError]		= useState<string | null>(null);
	// Hydrate search / sort / order / pageSize from the URL once on
	// mount so a bookmarked /admin/users?email=…&sort=… link lands on
	// the corresponding view.
	const [fSearch, fSetSearch]		= useState<string>(() => vSearchParams.get("email") ?? "");
	const [fSort,   fSetSort]		= useState<SortKey | null>(() => readSortFromParams(vSearchParams));
	const [fOrder,  fSetOrder]		= useState<SortOrder | null>(() => readOrderFromParams(vSearchParams));
	const [fPageSize, fSetPageSize]	= useState<number>(() => readPageSizeFromParams(vSearchParams));

	// Capture the latest search/sort/order/pageSize so the inline action
	// handlers can reload "the current view" without joining the
	// dependency chain of the debounced reload effect.
	const fParamsRef = useRef({ search: fSearch, sort: fSort, order: fOrder, pageSize: fPageSize });

	useEffect(() =>
	{
		fParamsRef.current = { search: fSearch, sort: fSort, order: fOrder, pageSize: fPageSize };
	}, [fSearch, fSort, fOrder, fPageSize]);

	// Mirror state to the URL on every edit. replace:true so keystrokes
	// don't pollute history; one Back click leaves the admin page.
	useEffect(() =>
	{
		fSetSearchParams(buildAdminSearchParams(fSearch, fSort, fOrder, fPageSize), { replace: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fSearch, fSort, fOrder, fPageSize]);

	const fLoad = useCallback((inPage: number, inSearch: string, inSort: SortKey | null, inOrder: SortOrder | null, inPageSize: number) =>
	{
		const vParams = new URLSearchParams();	// URL builder
		vParams.set("page",     String(inPage));
		vParams.set("pageSize", String(inPageSize));

		if (inSearch.trim() !== "") { vParams.set("email", inSearch.trim()); }
		if (inSort !== null)        { vParams.set("sort",  inSort); }
		if (inOrder !== null)       { vParams.set("order", inOrder); }

		api<UsersPageResponse>("GET", `/admin/users?${vParams.toString()}`)
			.then((vResp) =>
			{
				fSetUsers(vResp.items);
				fSetTotal(vResp.total);
				fSetPage(vResp.page);
			})
			.catch((vErr: unknown) =>
			{
				fSetError(vErr instanceof ApiError ? vErr.message : "Erreur réseau.");
			});
	}, []);

	// Search / sort / pageSize changes always reset to page 1;
	// debounced so each keystroke in the search box doesn't issue a
	// request. Sort and pageSize commits in one click — the delay is
	// invisible there.
	useEffect(() =>
	{
		const vTimer = window.setTimeout(() => fLoad(1, fSearch, fSort, fOrder, fPageSize), kSearchDebounceMs);

		return () => window.clearTimeout(vTimer);
	}, [fSearch, fSort, fOrder, fPageSize, fLoad]);

	const fReload = useCallback((inPage: number = fPage) =>
	{
		const vP = fParamsRef.current;	// Current view params

		fLoad(inPage, vP.search, vP.sort, vP.order, vP.pageSize);
	}, [fPage, fLoad]);

	// Click handler for sortable column headers. First click on a new
	// column lands on that column's default direction; subsequent
	// clicks on the same column toggle asc/desc.
	const fOnSort = (inKey: SortKey) =>
	{
		if (fSort !== inKey)
		{
			fSetSort(inKey);
			fSetOrder(kDefaultOrders[inKey]);
			return;
		}

		fSetOrder(fOrder === "asc" ? "desc" : "asc");
	};

	const fHandlePatch = async (inId: string, inBody: { role?: "USER" | "ADMIN"; disabled?: boolean; unlock?: true }) =>
	{
		try
		{
			await api("PATCH", `/admin/users/${encodeURIComponent(inId)}`, inBody);
			fReload();
		}
		catch (vErr)
		{
			alert(vErr instanceof ApiError ? vErr.message : "Erreur lors de la mise à jour.");
		}
	};

	const fHandleResendWelcome = async (inId: string, inEmail: string) =>
	{
		try
		{
			await api("POST", `/admin/users/${encodeURIComponent(inId)}/welcome`);
			alert(t("welcome_resent_ok", { email: inEmail }));
		}
		catch (vErr)
		{
			const vMessage = vErr instanceof ApiError ? vErr.message : tc("error_network");

			alert(`${t("welcome_resent_err", { email: inEmail })}\n${vMessage}`);
		}
	};

	const fHandleDelete = async (inId: string, inEmail: string) =>
	{
		const vOk = await fConfirm.confirm(
			{
				title:			t("delete_dialog_title"),
				entity:			inEmail,
				message:		t("delete_dialog_body"),
				confirmLabel:	t("btn_delete"),
			}
		);

		if (!vOk)
		{
			return;
		}

		try
		{
			await api("DELETE", `/admin/users/${encodeURIComponent(inId)}`);
			fReload();
		}
		catch (vErr)
		{
			alert(vErr instanceof ApiError ? vErr.message : "Erreur lors de la suppression.");
		}
	};

	if (fError !== null)
	{
		return (
			<ErrorState
				message={fError}
				onRetry={() => { fSetError(null); fReload(fPage); }}
			/>
		);
	}

	if (fUsers === null)
	{
		return (
			<div className="card">
				<Skeleton variant="title" widthPercent={30} />
				<SkeletonRows count={6} />
			</div>
		);
	}

	const vTotalPages	= Math.max(1, Math.ceil(fTotal / fPageSize));	// Total pages for the footer
	const vEffectiveSort	= fSort;										// null when no sort was chosen

	return (
		<>
			<CreateUserForm onCreated={() => fReload(1)} />

			<div className="card">
				<h2>{t("title_count", { total: fTotal })}</h2>

				<div className="filter-bar">
					<input
						type="text"
						placeholder={t("search_placeholder")}
						value={fSearch}
						onChange={(e) => fSetSearch(e.target.value)}
						aria-label={t("search_placeholder")}
					/>
					{(fSearch !== "" || fSort !== null)
						? (
							<button
								type="button"
								className="button button--ghost"
								onClick={() => { fSetSearch(""); fSetSort(null); fSetOrder(null); }}
							>
								{tc("clear")}
							</button>
						)
						: null}
				</div>

				<table>
					<thead>
						<tr>
							<SortableTh label={t("col_email")}		column="email"		current={vEffectiveSort} order={fOrder} onSort={fOnSort} />
							<SortableTh label={t("col_role")}		column="role"		current={vEffectiveSort} order={fOrder} onSort={fOnSort} />
							<SortableTh label={t("col_cards")}		column="cardCount"	current={vEffectiveSort} order={fOrder} onSort={fOnSort} />
							<SortableTh label={t("col_created")}	column="createdAt"	current={vEffectiveSort} order={fOrder} onSort={fOnSort} />
							<th>{t("col_status")}</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						{fUsers.map((u) => (
							<tr key={u.id}>
								<td>{u.email}{u.id === vMeId ? <span className="muted"> {t("you")}</span> : null}</td>
								<td>
									<span className={`tag ${u.role === "ADMIN" ? "tag--admin" : "tag--user"}`}>
										{u.role}
									</span>
								</td>
								<td>{u.cardCount}</td>
								<td>{formatDateTime(u.createdAt)}</td>
								<td>
									{u.disabledAt !== null
										? <span className="tag tag--disabled">{t("status_disabled")}</span>
										: isLocked(u)
											? <span className="tag tag--disabled" title={t("locked_until_tooltip", { date: formatDateTime(u.lockedUntil ?? 0), time: formatHm(u.lockedUntil ?? 0) })}>
												{t("status_locked")}
											</span>
											: <span className="muted">{t("status_active")}</span>}
								</td>
								<td style={{ whiteSpace: "nowrap" }}>
									{u.id !== vMeId
										? (
											<>
												<button
													type="button"
													className="button button--ghost"
													onClick={() => fHandlePatch(u.id, { role: u.role === "ADMIN" ? "USER" : "ADMIN" })}
												>
													{u.role === "ADMIN" ? t("btn_demote") : t("btn_promote")}
												</button>
												{" "}
												<button
													type="button"
													className="button button--ghost"
													onClick={() => fHandlePatch(u.id, { disabled: u.disabledAt === null })}
												>
													{u.disabledAt === null ? t("btn_disable") : t("btn_reactivate")}
												</button>
												{" "}
												{isLocked(u)
													? (
														<>
															<button
																type="button"
																className="button button--ghost"
																onClick={() => fHandlePatch(u.id, { unlock: true })}
															>
																{t("btn_unlock")}
															</button>
															{" "}
														</>
													)
													: null}
												<button
													type="button"
													className="button button--ghost"
													onClick={() => fHandleResendWelcome(u.id, u.email)}
												>
													{t("btn_resend_welcome")}
												</button>
												{" "}
												<button
													type="button"
													className="button button--danger"
													onClick={() => fHandleDelete(u.id, u.email)}
												>
													{t("btn_delete")}
												</button>
											</>
										)
										: <span className="muted">{tc("dash")}</span>}
								</td>
							</tr>
						))}
					</tbody>
				</table>

				<div className="pager">
					<select
						className="pager__page-size"
						value={fPageSize}
						onChange={(e) => fSetPageSize(Number.parseInt(e.target.value, 10))}
						aria-label={tc("page_size_label")}
					>
						{kPageSizeOptions.map((n) => (
							<option key={n} value={n}>{n}</option>
						))}
					</select>
					<button
						type="button"
						className="button button--ghost"
						disabled={fPage <= 1}
						onClick={() => fReload(fPage - 1)}
					>
						{tc("prev")}
					</button>
					<span className="muted">{tc("page_of", { page: fPage, total: vTotalPages })}</span>
					<button
						type="button"
						className="button button--ghost"
						disabled={fPage >= vTotalPages}
						onClick={() => fReload(fPage + 1)}
					>
						{tc("next")}
					</button>
				</div>
			</div>
			{fConfirm.dialog}
		</>
	);
}

/*
Clickable column header that drives sort state on the parent. When
the column is the active sort key, the label is suffixed with an
arrow (↑/↓) showing the current direction; otherwise nothing is
shown so unsorted columns still look neutral. Click toggles direction
when already active or switches to this column at its default
direction.
*/
function SortableTh(
	props: {
		label:		string;								// Column heading
		column:		SortKey;								// Sort key this column maps to
		current:	SortKey | null;						// Currently active sort key (null = server default)
		order:		SortOrder | null;					// Currently active direction
		onSort:		(inKey: SortKey) => void;			// Reports a click
	}
): JSX.Element
{
	const vIsActive = props.current === props.column;	// True when this column drives sorting
	const vArrow	= !vIsActive ? "" : props.order === "asc" ? " ↑" : " ↓";

	return (
		<th>
			<button
				type="button"
				className="th-sort"
				onClick={() => props.onSort(props.column)}
				aria-sort={vIsActive ? (props.order === "asc" ? "ascending" : "descending") : "none"}
			>
				{props.label}{vArrow}
			</button>
		</th>
	);
}

/*
Reads the sort key from URL search params, validating against the
SortKey union so an arbitrary URL can't drive the table into an
unknown state. Returns null when absent or unrecognised (the parent
treats null as "use the server default").
*/
function readSortFromParams(
	inParams:	URLSearchParams		// Router search params
): SortKey | null
{
	const vRaw = inParams.get("sort");	// Raw "?sort=" value

	switch (vRaw)
	{
		case "email":
		case "role":
		case "createdAt":
		case "cardCount":
			return vRaw;
		default:
			return null;
	}
}

/*
Reads the sort direction from URL search params. Returns null on
anything other than "asc" / "desc"; the parent then defers to the
key's per-column default (kDefaultOrders).
*/
function readOrderFromParams(
	inParams:	URLSearchParams		// Router search params
): SortOrder | null
{
	const vRaw = inParams.get("order");	// Raw "?order=" value

	return vRaw === "asc" || vRaw === "desc" ? vRaw : null;
}

/*
Reads pageSize from URL search params, validating against the
allowed selector options to avoid letting an arbitrary URL widen
the per-request payload past the cap. Falls back to default on any
unexpected value.
*/
function readPageSizeFromParams(
	inParams:	URLSearchParams		// Router search params
): number
{
	const vRaw = inParams.get("pageSize");	// Raw "?pageSize=" value

	if (vRaw === null)
	{
		return kPageSizeDefault;
	}

	const vN = Number.parseInt(vRaw, 10);	// Parsed integer

	return kPageSizeOptions.includes(vN) ? vN : kPageSizeDefault;
}

/*
Serialises the current search / sort / order / pageSize state to
URLSearchParams. Empty / default values are omitted so the typical
clean view produces /admin/users with no query string.
*/
function buildAdminSearchParams(
	inSearch:	string,				// Current email substring
	inSort:		SortKey | null,		// Active sort key (null = server default)
	inOrder:	SortOrder | null,	// Active sort direction
	inPageSize:	number				// Current rows per page
): URLSearchParams
{
	const vParams = new URLSearchParams();	// URL builder

	if (inSearch.trim() !== "")			{ vParams.set("email",    inSearch.trim()); }
	if (inSort !== null)				{ vParams.set("sort",     inSort); }
	if (inOrder !== null)				{ vParams.set("order",    inOrder); }
	if (inPageSize !== kPageSizeDefault){ vParams.set("pageSize", String(inPageSize)); }

	return vParams;
}

/*
Inline form to create a new user. Authentication is passwordless —
only the email + role are needed. The created user signs in via
/auth/request-code on their email; no out-of-band invitation
artefact is emitted.
*/
function CreateUserForm(
	props:	{ onCreated: () => void }	// Notified on success so the parent reloads
): JSX.Element
{
	const { t }		= useTranslation("admin_users");
	const { t: tc }	= useTranslation("common");

	const [fEmail,	fSetEmail]	= useState<string>("");
	const [fRole,	fSetRole]	= useState<"USER" | "ADMIN">("USER");
	const [fError,	fSetError]	= useState<string | null>(null);
	const [fBusy,	fSetBusy]	= useState<boolean>(false);

	const fHandleSubmit = async (inEvent: FormEvent<HTMLFormElement>) =>
	{
		inEvent.preventDefault();
		fSetError(null);
		fSetBusy(true);

		try
		{
			await api("POST", "/admin/users", { email: fEmail, role: fRole });
			fSetEmail("");
			fSetRole("USER");
			props.onCreated();
		}
		catch (vErr)
		{
			fSetError(vErr instanceof ApiError ? vErr.message : tc("error_network"));
		}
		finally
		{
			fSetBusy(false);
		}
	};

	return (
		<form className="card" onSubmit={fHandleSubmit}>
			<h2>{t("create_title")}</h2>
			<div className="form-grid">
				<div>
					<label htmlFor="new-email">{t("create_email")}</label>
					<input
						id="new-email"
						type="email"
						autoComplete="off"
						value={fEmail}
						onChange={(e) => fSetEmail(e.target.value)}
						required
					/>
				</div>
				<div>
					<label htmlFor="new-role">{t("create_role")}</label>
					<select id="new-role" value={fRole} onChange={(e) => fSetRole(e.target.value as "USER" | "ADMIN")}>
						<option value="USER">USER</option>
						<option value="ADMIN">ADMIN</option>
					</select>
				</div>
				<div style={{ display: "flex", alignItems: "flex-end" }}>
					<button type="submit" className="button" disabled={fBusy}>
						{fBusy ? t("create_progress") : t("create_submit")}
					</button>
				</div>
			</div>
			{fError !== null ? <div className="error">{fError}</div> : null}
		</form>
	);
}

/*
Formats a ms-since-epoch timestamp to "YYYY-MM-DD" in the local TZ.
*/
function formatDateTime(
	inMs:	number	// Timestamp in milliseconds
): string
{
	const vDate = new Date(inMs);	// Local Date
	const pad = (n: number) => String(n).padStart(2, "0");

	return `${vDate.getFullYear()}-${pad(vDate.getMonth() + 1)}-${pad(vDate.getDate())}`;
}

/*
Formats a ms-since-epoch timestamp to "HH:MM" in the local TZ. Used
in the lockout tooltip alongside formatDateTime.
*/
function formatHm(
	inMs:	number	// Timestamp in milliseconds
): string
{
	const vDate = new Date(inMs);	// Local Date
	const pad = (n: number) => String(n).padStart(2, "0");

	return `${pad(vDate.getHours())}:${pad(vDate.getMinutes())}`;
}

/*
Returns true when the user row currently sits inside a lockout
window. The server clears lockedUntil on the next successful login
and on admin unlock, so a stale "lockedUntil in the past" value is
treated as "not locked" for display purposes.
*/
function isLocked(
	inRow:	UserRow	// User row from /admin/users
): boolean
{
	return inRow.lockedUntil !== null && inRow.lockedUntil > Date.now();
}
